import { defineStore } from 'pinia'
import { reactive } from 'vue'
import cloneDeep from 'lodash/cloneDeep'

type Nullable<T> = T | null

export interface IUserStoreState {
	user: Nullable<IUserModel>
}

// type Nullable<T> = { [K in keyof T]: T[K] | null }

export interface IUserStore {
	readonly state: {
		user: Nullable<IUserModel>
	}
	setUser: (user: Nullable<IUserModel>) => void
	login: (login: string, password: string) => Promise<IUserModel>
	loginByToken: (token: string) => Promise<IUserModel>
	loginBD: (email: string, password: string) => Promise<IUserModel>
	updateUser: (user: Partial<IUserModel>) => Promise<IUserModel>
	register: (
		email: string,
		password: string,
		phoneNumber: string,
		name: string,
		lastName: string,
		consents: IUserConsentsModel,
	) => Promise<IUserModel>
	logout: () => void
	fetchUser: () => Promise<IUserModel>
	isEmailVerified: ComputedRef<boolean>
	getUser: ComputedRef<IUserModel | null>
	isLoggedIn: ComputedRef<boolean>
	isAdmin: ComputedRef<boolean | undefined>
	getPermission: (permission: EUserPermissions | EUserPermissions[]) => boolean | undefined
}

export const useUserStore = defineStore('userStore', (): IUserStore => {
	// const config = useRuntimeConfig()
	// const localePath = useLocalePath()
	const _state = reactive<IUserStoreState>({
		user: null,
	})

	const setUser = (user: Nullable<IUserModel>) => {
		_state.user = cloneDeep(user)
	}

	const login = async (login: string, password: string) => {
		await ApiService.login(login, password)

		const user = await fetchUser()
		const cookie = useCookie('is_logged')
		cookie.value = 'true'
		if (!user) logout()
		return user
	}

	const loginByToken = async (token: string) => {
		await ApiService.setCookies()
		await ApiService.loginByCode(token)
		const user = await fetchUser()
		const cookie = useCookie('is_logged')
		cookie.value = 'true'
		if (!user) logout()
		return user
	}

	const loginBD = async (email: string, password: string) => {
		await ApiService.setCookies()
		await ApiService.loginBD(email, password)
		const user = await fetchUser()
		const cookie = useCookie('is_logged')
		cookie.value = 'true'
		// await fetchCounters()
		return user
	}

	const register = async (email: string, password: string, phoneNumber: string, name: string, lastName: string, consents: IUserConsentsModel) => {
		await ApiService.register(email, password, phoneNumber, name, lastName, consents)
		await ApiService.login(email, password)

		const cookie = useCookie('is_logged')
		cookie.value = 'true'
		const user = await fetchUser()
		return user
	}

	const updateUser = async (user: Partial<IUserModel>) => {
		// The API always requires notifications and consents to be present on profile updates,
		// so fall back to the currently stored values whenever a caller doesn't provide them.
		const payload: Partial<IUserModel> = {
			notifications: _state.user?.notifications ?? null,
			consents: _state.user?.consents ?? null,
			...user,
		}
		const updatedUser = await ApiService.updateUser(payload)
		setUser(updatedUser)
		return updatedUser
	}

	const fetchUser = async () => {
		const user = await ApiService.fetchUser()
		setUser(user)
		// if (user?.suspendedAt) logout()
		return user
	}

	const logout = async () => {
		// const router = useRouter()
		// router.push('/')

		const cookie = useCookie('is_logged')
		_state.user = null
		cookie.value = 'false'

		await ApiService.logout()
	}

	const isEmailVerified = computed(() => {
		return !!_state.user?.emailVerifiedAt
	})

	const getUser = computed(() => {
		return _state.user
	})

	const isLoggedIn = computed(() => {
		return !!_state.user
	})

	const isAdmin = computed(() => {
		if (!_state.user) return false
		return _state.user.permissions.includes(EUserPermissions.ADMIN)
	})

	const getPermission = (permission: EUserPermissions | EUserPermissions[]) => {
		if (Array.isArray(permission)) {
			return permission.some((perm) => _state.user?.permissions.includes(perm))
		}
		return _state.user?.permissions.includes(permission)
	}

	return {
		state: _state,
		setUser,
		login,
		loginByToken,
		loginBD,
		register,
		updateUser,
		logout,
		fetchUser,
		isEmailVerified,
		getUser,
		isLoggedIn,
		isAdmin,
		getPermission,
	}
})
